import { FunctionComponent, useEffect } from 'react';
import { taskZod } from 'packages/lib/api/annotations/contract/task';
import { apiGet } from 'packages/lib/api/annotations/rest';
import { useHistory } from 'react-router-dom';
import useSWR from 'swr';
import {
  instructionAtom,
  sampleGroupAtom,
  taskAtom,
  taskGroupAtom,
  toolAliasAtom,
} from 'packages/at-shell/state/recoil/task';
import { useSetRecoilState } from 'recoil';
import { dispatchErrorToast } from 'packages/lib/common-ui/toast/api-toast';
import { taskGroupZod } from 'packages/lib/api/annotations/contract/task-group';
import { extractionTokenAtom } from 'packages/at-shell/state/recoil/extraction';
import { instructionZod } from 'packages/lib/api/annotations/contract/instruction';
import { sampleGroupZod } from 'packages/lib/api/annotations/contract/sample-group';
import { useToolQueryparams } from 'packages/at-shell/utils/tool-query-params';
import { devModeAtom } from 'packages/at-shell/state/recoil/ui';
import { toolAliasRegex } from 'packages/task-man/components/sample-group-task-queues';
import { useToolParams } from 'packages/at-shell/utils/use-tool-params';

export const SyncAnnotationRecoilParams: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  /**
   * ANCHOR - URL Route Params
   */
  const { task_token, extraction_token } = useToolParams();

  const { dev_mode } = useToolQueryparams();

  /**
   * ANCHOR – Tool Alias
   */
  const history = useHistory();
  const {
    location: { pathname },
  } = history;
  const toolAlias = pathname.match(toolAliasRegex)?.groups?.tool_alias;
  const setToolAlias = useSetRecoilState(toolAliasAtom);
  useEffect(() => {
    setToolAlias(toolAlias);
  }, [toolAlias, setToolAlias]);

  /**
   * ANCHOR – Developer Mode
   */
  const setDevMode = useSetRecoilState(devModeAtom);
  useEffect(() => {
    setDevMode(dev_mode);
  }, [dev_mode, setDevMode]);

  /**
   * ANCHOR - Extraction Token
   */
  const setExtractionTokenAtom = useSetRecoilState(extractionTokenAtom);
  useEffect(() => {
    setExtractionTokenAtom(extraction_token);
  }, [extraction_token, setExtractionTokenAtom]);

  /**
   * ANCHOR - Task
   */
  const { data: task, error: taskError } = useSWR(
    !task_token ? null : `/tasks/${task_token}`,
    (apiPath: string) => apiGet(apiPath, taskZod.parse)
  );
  const setTaskAtom = useSetRecoilState(taskAtom);
  useEffect(() => {
    if (!taskError) {
      setTaskAtom(task);
    } else {
      dispatchErrorToast(
        `There was an issue fetching your task data: ${taskError}`
      );
      setTaskAtom(null);
    }
  }, [task, taskError, setTaskAtom]);

  /**
   * ANCHOR - Task Group
   */
  const { data: taskGroup, error: taskGroupError } = useSWR(
    !task?.taskGroupToken ? null : `/task-groups/${task.taskGroupToken}`,
    (apiPath: string) => apiGet(apiPath, taskGroupZod.parse)
  );
  const setTaskGroupAtom = useSetRecoilState(taskGroupAtom);
  useEffect(() => {
    if (!taskGroupError) {
      setTaskGroupAtom(taskGroup);
    } else {
      dispatchErrorToast(
        `There was an issue fetching your task group data: ${taskGroupError}`
      );
      setTaskGroupAtom(null);
    }
  }, [taskGroup, taskGroupError, setTaskGroupAtom]);

  /**
   * ANCHOR - Instruction
   */
  const { data: instruction, error: instructionError } = useSWR(
    !task?.instructionToken ? null : `/instructions/${task.instructionToken}`,
    (apiPath: string) => apiGet(apiPath, instructionZod.parse)
  );
  const setInstructionAtom = useSetRecoilState(instructionAtom);
  useEffect(() => {
    if (!instructionError) {
      setInstructionAtom(instruction);
    } else {
      dispatchErrorToast(
        `There was an issue fetching your task instruction data: ${instructionError}`
      );
      setInstructionAtom(null);
    }
  }, [instruction, instructionError, setInstructionAtom, setTaskGroupAtom]);

  /**
   * ANCHOR - Sample Group
   */
  const { data: sampleGroup, error: sampleGroupError } = useSWR(
    !taskGroup?.sampleGroupToken
      ? null
      : `/sample-groups/${taskGroup.sampleGroupToken}`,
    (apiPath: string) => apiGet(apiPath, sampleGroupZod.parse)
  );
  const setSampleGroupAtom = useSetRecoilState(sampleGroupAtom);
  useEffect(() => {
    if (!sampleGroupError) {
      setSampleGroupAtom(sampleGroup);
    } else {
      dispatchErrorToast(
        `There was an issue fetching your sample group data: ${sampleGroupError}`
      );
      setSampleGroupAtom(null);
    }
  }, [sampleGroup, sampleGroupError, setSampleGroupAtom, setTaskGroupAtom]);
  return null;
};
